/*
 * Lista de órdenes de traspaso.
 *
 * Lista de traspasos paginada. Muestra la opción para poder crear
 * un nuevo registro de orden de traspaso. Los filtros en este lista son: Estatus,
 * Origen, Fecha de creación y Destino, así como un buscador por palabras general.
 *
 * Created on Tue Mar 21 2023 by José Luis Chávez Ayala.
 *
 * Copyright (c) 2023 Turn My App.
 */

<template>
  <div>

    <!-- #region::Searcher, filters and actions buttons -->
    <TransfersListFilters
      :can-manage-transfers="canManageTransfers"
      @on:filter="handleFilter"
    />
    <!-- #endregion::Searcher, filters and actions buttons -->

    <!-- #region::Skeleton -->
    <template v-if="isLoadingTransfers">
      <b-skeleton
        class="mt-1"
        animation="wave"
        width="100%"
        height="430px"
      />
    </template>
    <!-- #endregion::Skeleton -->

    <template v-else>
      <b-card
        class="mt-1 border border-dark shadow-none"
        no-body
      >
        <b-card-text>

          <!-- #region::Transfers list table -->
          <b-table
            v-if="availableTransfers"
            :items="transfers"
            :fields="columns"
            responsive
            class="my-0"
          >

            <!-- #region:Virtual column for index -->
            <template #cell(No)="data">
              {{ indexFrom + data.index }}
            </template>
            <!-- #endregion:Virtual column for index -->

            <!-- #region:Virtual column for ID -->
            <template #cell(ID)="{ item }">
              <router-link :to="{ name: 'transfer-details', params: { id: item.IdTransfer } }">
                {{ item.ID }}
              </router-link>
            </template>
            <!-- #endregion:Virtual column for ID -->

            <!-- #region::Virtual column for created date -->
            <template #cell(created_at)="{ item }">
              {{ item.created_at | moment("DD/MM/YYYY hh:mm a") }}
            </template>
            <!-- #endregion::Virtual column for created date -->

            <!-- #region::Virtual column for products count -->
            <template #cell(Qty)="{ item }">
              <span class="text-nowrap">{{ item.Qty | placeholder('Sin especificar') }}</span>
            </template>
            <!-- #endregion::Virtual column for products count -->

            <!-- #region::Virtual column for status order -->
            <template #cell(StatusText)="{ item }">
              <template v-if="item.StatusText">
                <div
                  :class="[statusBorderStyle(item.StatusText), statusColorStyle(item.StatusText)]"
                  role="alert"
                  class="text-nowrap"
                  style="display: inline; font-size: 11px;"
                >
                  {{ item.StatusText }}
                </div>
              </template>
              <span
                v-else
                class="text-nowrap"
              >Sin especificar</span>
            </template>
            <!-- #endregion::Virtual column for status order -->

          </b-table>
          <!-- #endregion::Transfers list table -->

          <!-- #region::Alert when no transfers are available -->
          <b-alert
            v-if="!availableTransfers && !isLoadingTransfers"
            variant="warning"
            class="my-0"
            show
          >
            <div class="alert-body">
              <span>No se encontraron órdenes de traspaso.</span>
            </div>
          </b-alert>
          <!-- #endregion::Alert when no transfers are available -->

        </b-card-text>
      </b-card>
    </template>

    <!-- #region begin::Pagination & items per list -->
    <BasicPaginator
      v-if="availableTransfers"
      ref="basic-paginator"
      class="mt-2"
      :callback="handleChangePagination"
      :total-rows="totalTransfers"
    />
    <!-- #endregion end::Pagination & items per list -->

  </div>
</template>

<script>
// #region Imports
import { mapGetters, mapActions } from 'vuex'
import {
  BSkeleton, BCard, BCardText, BTable, BAlert,
} from 'bootstrap-vue'

import BasicPaginator from '@/components/tables/BasicPaginator.vue'
import TransfersListFilters from '@/modules/production/transfers/components/TransfersListFilters.vue'

import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import getError from '@/helpers/ErrorsHandler'
import { adminLevel, ADMIN_USER_LEVELS } from '@/helpers/UserLevel'
// #endregion

export default {
  components: {
    BCard,
    BTable,
    BAlert,
    BCardText,
    BSkeleton,
    BasicPaginator,
    TransfersListFilters,
  },
  data() {
    return {
      lastPage: 0,
      indexFrom: 0,
      totalTransfers: 0,
      columns: [
        {
          key: 'No',
          label: 'No.',
          thStyle: 'text-transform: none;',
          thClass: 'bg-light',
        },
        {
          key: 'ID',
          label: 'ID',
          thStyle: 'text-transform: none;',
          thClass: 'bg-light',
        },
        {
          key: 'created_at',
          label: 'Creada',
          thStyle: 'text-transform: none;',
          thClass: 'bg-light',
        },
        {
          key: 'Qty',
          label: 'No. de productos',
          thStyle: 'text-transform: none;',
          thClass: 'bg-light',
        },
        {
          key: 'LocationOrigin',
          label: 'Origen',
          thStyle: 'text-transform: none;',
          thClass: 'bg-light',
        },
        {
          key: 'LocationDestiny',
          label: 'Destino',
          thStyle: 'text-transform: none;',
          thClass: 'bg-light',
        },
        {
          key: 'StatusText',
          label: 'Estatus',
          thStyle: 'text-transform: none;',
          thClass: 'bg-light',
        },
      ],

      transfers: [],
      isLoadingTransfers: false,
      adminLevel,
      ADMIN_USER_LEVELS,
    }
  },
  computed: {
    ...mapGetters({
      /**
       * Filtros globales
       */
      getFilteredSortBy: 'filters/getFilteredSortBy',
      getFilteredOrigin: 'filters/getFilteredOrigin',
      getFilteredStatus: 'filters/getFilteredStatus',
      getFilteredPerPage: 'filters/getFilteredPerPage',
      getFilteredSearchTerm: 'filters/getFilteredSearchTerm',
      getFilteredCurrentPage: 'filters/getFilteredCurrentPage',
      getFilteredDestination: 'filters/getFilteredDestination',
      getFilteredCreatedAtDate: 'filters/getFilteredCreatedAtDate',
    }),
    /**
     * Ordenamiento global (filtro en store)
     */
    filteredSortBy: {
      get() { return this.getFilteredSortBy },
      set(value) { this.setFilteredSortBy(value) },
    },
    /**
     * Origen global (filtro en store)
     */
    filteredOrigin: {
      get() { return this.getFilteredOrigin },
      set(value) { this.setFilteredOrigin(value) },
    },
    /**
     * Estatus global (filtro en store)
     */
    filteredStatus: {
      get() { return this.getFilteredStatus },
      set(value) { this.setFilteredStatus(value) },
    },
    /**
     * Indica la cantidad de elementos por página (paginado global)
     */
    filteredPerPage: {
      get() { return this.getFilteredPerPage },
      set(value) { this.setFilteredPerPage(value) },
    },
    /**
     * Término de búsqueda filtrado (filtro en store)
     */
    filteredSearchTerm: {
      get() { return this.getFilteredSearchTerm },
      set(value) { this.setFilteredSearchTerm(value) },
    },
    /**
     * Indica la página actual (paginado global)
     */
    filteredCurrentPage: {
      get() { return this.getFilteredCurrentPage },
      set(value) { this.setFilteredCurrentPage(value) },
    },
    /**
     * Destino global (filtro en store)
     */
    filteredDestination: {
      get() { return this.getFilteredDestination },
      set(value) { this.setFilteredDestination(value) },
    },
    /**
     * Fecha de creación global (filtro en store)
     */
    filteredCreatedAtDate: {
      get() { return this.getFilteredCreatedAtDate },
      set(value) { this.setFilteredCreatedAtDate(value) },
    },
    availableTransfers() {
      return this.totalTransfers > 0
    },
    /**
     * Indica si el usuario puede gestionar traspasos
     */
    canManageTransfers() {
      return this.adminLevel() === this.ADMIN_USER_LEVELS.ADMINISTRADOR
    },
  },

  /**
  * Hook que se ejecuta cuando el componente es creado
  *
  * @summary Verifica si hay parámetros en la ruta provenientes del dashboard
  * y carga la lista de traspasos
  */
  async created() {
    // Verify if are params in route from dashboard
    if (this.$store.state.dashboard.paramsDashboard) {
      this.filteredStatus = this.$store.state.dashboard.paramsDashboard
      this.listenerParmasDashboard('')
    }

    this.loadTransfersListWithVerification()
  },
  methods: {
    ...mapActions({
      loadTransfers: 'transfers/loadTransfers',
      listenerParmasDashboard: 'dashboard/listenerParmasDashboard',

      /**
       * Filtros globales
       */
      setFilteredSortBy: 'filters/setFilteredSortBy',
      setFilteredOrigin: 'filters/setFilteredOrigin',
      setFilteredStatus: 'filters/setFilteredStatus',
      setFilteredPerPage: 'filters/setFilteredPerPage',
      setFilteredSearchTerm: 'filters/setFilteredSearchTerm',
      setFilteredCurrentPage: 'filters/setFilteredCurrentPage',
      setFilteredDestination: 'filters/setFilteredDestination',
      setFilteredCreatedAtDate: 'filters/setFilteredCreatedAtDate',
    }),

    /**
    * Evento de filtrado
    *
    * @summary Evento del componente de filtrado. Devuelve los registros en base
    * al tipo de filtro y recarga la lista de traspasos
    * @param filter - Objeto con el tipo y valor del filtro
    */
    async handleFilter(filter) {
      switch (filter.type) {
        case 'sort': this.filteredSortBy = filter.value
          break
        case 'date': this.filteredCreatedAtDate = filter.value
          break
        case 'status': this.filteredStatus = filter.value
          break
        case 'origin': this.filteredOrigin = filter.value
          break
        case 'destination': this.filteredDestination = filter.value
          break
        default: this.filteredSearchTerm = filter.value
          this.filteredCurrentPage = 1
          // eslint-disable-next-line no-unused-expressions
          this.$refs['basic-paginator']?.resetCurrentPage()
          break
      }

      this.loadTransfersListWithVerification()
    },

    /**
    * Carga de elementos de la lista con verificación de paginado
    *
    * @summary Carga la lista de elementos y verifica que la página actual contenga elementos
    */
    async loadTransfersListWithVerification() {
      await this.loadTransfersList()
      this.verifyContenList()
    },

    /**
    * Evento de paginación
    *
    * @summary Evento del componente de paginación. Establece la página actual y la cantidad
    * de registros por página
    * @param {number} currentPage - Página actual
    * @param {number} perPage - Elementos a mostrar por página
    */
    async handleChangePagination(currentPage, perPage) {
      this.filteredCurrentPage = currentPage
      this.filteredPerPage = perPage
      this.loadTransfersList()
    },

    /**
    * Verificación de contenido
    *
    * @summary Verifica que la página actual tenga registros, en caso de no tenerlos
    * tomará la última página disponible y cargará los registros
    */
    async verifyContenList() {
      if (this.transfers.length === 0) {
        this.filteredCurrentPage = this.lastPage

        // Verifica si hay registros disponibles en el servidor
        if (this.availableTransfers) {
          this.$refs['basic-paginator'].resetCurrentPage(this.lastPage)
          await this.loadTransfersList()
        }
      }
    },

    /**
    * Carga de registros de tipo traspasos
    *
    * @summary ⁡⁢⁣⁡⁢⁢⁡⁣⁣⁡⁣⁢⁣⁡⁣⁢⁢⁡⁢⁢⁢‍Carga y establece los registros de traspasos y los parámetros de paginación⁡⁡⁡⁡⁡⁡
    * @exception ⁡⁢⁣⁢Error de validación en la carga de registros de traspasos⁡
    */
    async loadTransfersList() {
      try {
        this.$swal({
          title: 'Cargando...',
          allowOutsideClick: false,
        })
        this.$swal.showLoading()
        this.isLoadingTransfers = true

        const response = await this.loadTransfers(this.loadParams())
        this.transfers = response.data.data.data
        this.totalTransfers = response.data.data.total
        this.indexFrom = response.data.data.from
        this.lastPage = response.data.data.last_page

        this.isLoadingTransfers = false
        this.$swal.close()
      } catch (error) {
        this.$swal.close()
        this.showToast('Error de validación', getError(error), 'danger')
      }
    },

    /**
    * Parámetros de carga
    *
    * @summary Devuelve los parámetros de carga de la lista
    */
    loadParams() {
      return {
        perPage: this.filteredPerPage,
        sortTerm: this.filteredSortBy,
        currentPage: this.filteredCurrentPage,
        statusTerm: this.filteredStatus,
        originTerm: this.filteredOrigin,
        searchTerm: this.filteredSearchTerm,
        dateTerm: this.filteredCreatedAtDate,
        destinationTerm: this.filteredDestination,
      }
    },

    /**
    * Establece el estilo de banner para el estado del traspaso
    *
    * @param {string} status - Estado del traspaso
    */
    statusBorderStyle(status) {
      switch (status) {
        case 'Completada':
          return 'sm-banner-alert-bordered'
        default:
          return 'sm-banner-alert'
      }
    },

    /**
    * Establece el color de banner para el estado del traspaso
    *
    * @param {string} status - Estado del traspaso
    */
    statusColorStyle(status) {
      switch (status) {
        case 'Solicitud':
          return 'alert-purple'
        case 'Traspaso':
          return 'alert-pink'
        case 'Aprobada':
          return 'alert-green'
        case 'Rechazada':
          return 'alert-orange'
        case 'En transito':
          return 'alert-yellow-black'
        case 'Listo para traspaso':
          return 'alert-marine'
        case 'Recibida':
          return 'alert-blue'
        default:
          return 'alert-gray'
      }
    },

    /**
    * Muestra un mensaje de tipo toast
    *
    * @param {string} title - Título del mensaje
    * @param {string} text - Contenido del mensaje
    * @param {string} variant - Tipo del mensaje (warning, success, danger)
    */
    showToast(title, text, variant) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title, text, variant, icon: 'BellIcon',
        },
      })
    },
  },
}
</script>
